import { useState, useMemo } from 'react';
import { CheckCircle, UserPlus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { cn, getInitials } from '@/lib/utils';
import {
  useAssignedPlayers,
  useAddPlayersToSession,
} from '@/hooks/use-attendance';

interface AddPlayersSheetProps {
  sessionId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function AddPlayersSheet({
  sessionId,
  open,
  onOpenChange,
}: AddPlayersSheetProps) {
  const { data: available } = useAssignedPlayers(sessionId);
  const addPlayers = useAddPlayersToSession(sessionId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return available.filter((p) =>
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(q)
    );
  }, [available, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      await addPlayers.mutate(Array.from(selected));
      setSelected(new Set());
      setSearch('');
      onOpenChange(false);
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    setSelected(new Set());
    setSearch('');
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="bottom" className="h-[80vh] flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-3 border-b">
          <SheetTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Add Players
          </SheetTitle>
        </SheetHeader>

        {/* Search */}
        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search players..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Player list */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm py-8">
              {available.length === 0
                ? 'All assigned players are already in this session'
                : 'No players match your search'}
            </p>
          ) : (
            <div className="space-y-1">
              {filtered.map((player) => {
                const isSelected = selected.has(player.id);
                return (
                  <div
                    key={player.id}
                    onClick={() => toggle(player.id)}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors',
                      isSelected
                        ? 'bg-primary/10 border border-primary/30'
                        : 'hover:bg-muted border border-transparent'
                    )}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                      {getInitials(player.firstName, player.lastName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">
                        {player.firstName} {player.lastName}
                      </div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {player.level || 'Unassigned'}
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-4 border-t">
          <Button
            onClick={handleAdd}
            disabled={selected.size === 0 || adding}
            className="w-full"
          >
            {adding
              ? 'Adding...'
              : selected.size === 0
              ? 'Select players to add'
              : `Add ${selected.size} player${selected.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
